import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from '@quorum/api';
import {
  Address,
  Associate,
  AssociateReport,
  Customer,
  Location,
  Vehicle,
  ServiceAppointment as Appointment,
  VehicleBody,
} from '@quorum/models/xs-resource';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';
import { CustomValidators } from '@quorum/sha-validators';
import { VehicleOwnerCreateDialogComponent } from 'libs/sha-associates-ng-ui/src/lib/vehicle-owner-create-dialog/vehicle-owner-create-dialog.component';
import { BehaviorSubject, Observable } from 'rxjs';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

@Component({
  selector: 'svc-edit-vehicle-dialog',
  templateUrl: './edit-vehicle-dialog.component.html',
  styleUrls: ['./edit-vehicle-dialog.component.scss'],
})
export class EditVehicleDialogComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;
  typedName: string;
  selectedAssoc: AssociateReport;
  maxDate: Date = new Date(new Date().setFullYear(new Date().getFullYear() + 5));
  numberMask = createNumberMask({
    prefix: '',
    suffix: '',
  });
  formSubmitted = false;

  constructor(
    public dialogRef: MatDialogRef<EditVehicleDialogComponent>,
    public dialog: MatDialog,
    private router: Router,
    public serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    public serviceLaneFacade: ServiceLaneFacade,
    private apiService: ApiService,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: {
      vehicleForm: FormGroup;
      associateForm: FormGroup;
      addressForm: FormGroup;
      vehicleDropdowns: any;
      bodyDropdowns$: Observable<VehicleBody[]>;
      vehicle: Vehicle;
      appointment: Appointment;
      locations$: any;
      filteredAssociates$: BehaviorSubject<AssociateReport[]>;
    }
  ) {}

  ngOnInit(): void {
    this.dialogData.vehicleForm
      .get('make')
      .setValidators([Validators.required, CustomValidators.noWhitespaceValidator]);
    this.dialogData.vehicleForm.get('make').updateValueAndValidity({ emitEvent: false });
    this.dialogData.vehicleForm
      .get('model')
      .setValidators([Validators.required, CustomValidators.noWhitespaceValidator]);
    this.dialogData.vehicleForm.get('model').updateValueAndValidity({ emitEvent: false });
    this.dialogData.vehicleForm
      .get('name')
      .setValidators([Validators.required, CustomValidators.noWhitespaceValidator]);
    this.dialogData.vehicleForm.get('name').updateValueAndValidity({ emitEvent: false });

    // disable navigate and close modal if browser's back button clicked
    history.pushState(null, '');
    window.addEventListener('popstate', () => {
      this.cancel();
    });
  }

  saveTypedName(name: string) {
    this.typedName = name;
  }

  selectAssociate(value: any, filteredAssociates?: AssociateReport[]) {
    if (value == 'addAssociate') {
      this.selectedAssoc = null;
      this.dialogData.vehicleForm.get('name').setValue(null);
      const dialogRef = this.dialog.open(VehicleOwnerCreateDialogComponent, {
        disableClose: true,
        width: '800px',
        data: {
          associateDropdowns$: this.serviceLaneDropdownFacade.associateDropdowns$,
          addressDropdowns$: this.serviceLaneDropdownFacade.addressDropdowns$,
          locations$: this.dialogData.locations$,
          currentAssociate$: this.serviceLaneFacade.customerAssociate$,
          currentAddress$: this.serviceLaneFacade.customerAddress$,
          typedName: this.typedName ? this.typedName : '',
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        this.autocompleteTrigger.closePanel();
        this.typedName = '';

        if (result) {
          this.apiService
            .post<Associate>(`v/1/associates/associates`, result.newAssociate)
            .subscribe((newAssociate: Associate) => {
              const associateId = newAssociate.id;
              if (!result.newAddress.locationId.id) {
                const newLocation: Location = {
                  ...new Location(),
                  countryId: result.newAddress.countryId,
                  description: result.newAddress.locationId,
                  postalCode: null,
                  provStateId: result.newAddress.provStateId,
                };

                this.apiService
                  .post<Location>('v/1/associates/locations', newLocation)
                  .subscribe((newlyCreatedLocation) => {
                    const updatedAddress: Address = {
                      ...result.newAddress,
                      associateId: associateId,
                      locationId: newlyCreatedLocation.id, //needs change
                      cellPhoneNumber: result.newAddress.cellPhoneNumber
                        ? result.newAddress.cellPhoneNumber.replace(/\D+/g, '')
                        : null,
                      homePhoneNumber: result.newAddress.homePhoneNumber
                        ? result.newAddress.homePhoneNumber.replace(/\D+/g, '')
                        : null,
                    };

                    this.apiService
                      .post<Address>(`v/1/associates/associates/${associateId}/addresses`, updatedAddress)
                      .subscribe((newAddress: Address) => {
                        this.dialogData.associateForm.patchValue(
                          {
                            ...this.dialogData.associateForm,
                            id: newAssociate.id,
                            firstName: newAssociate.firstName,
                            lastName: newAssociate.lastName,
                            emailAddress: newAssociate.emailAddress,
                            classId: newAssociate.classId,
                            messagePreferenceId: newAssociate.messagePreferenceId,
                          },
                          { emitEvent: false }
                        );
                        this.dialogData.addressForm.patchValue({
                          ...this.dialogData.addressForm,
                          associateId: newAssociate.id,
                          addressLine1: newAddress.addressLine1,
                          homePhoneNumber: newAddress.homePhoneNumber,
                          cellPhoneNumber: newAddress.cellPhoneNumber,
                          countryId: newAddress.countryId,
                          postalZipCode: newAddress.postalZipCode,
                          provStateId: newAddress.provStateId,
                          locationId: newAddress.locationId,
                        });

                        this.dialogData.vehicleForm.patchValue({
                          associateId: newAssociate.id,
                          name: `${newAssociate.lastName}, ${newAssociate.firstName}`,
                        });

                        const customer: Customer = {
                          ...new Customer(),
                          associateId: newAssociate.id,
                        };

                        this.apiService.post<Customer>(`v/1/associates/customers`, customer).subscribe();

                        this.serviceLaneDropdownFacade.getLocationsFromServer();
                      });
                  });
              } else {
                const updatedAddress: Address = {
                  ...result.newAddress,
                  associateId: associateId,
                  locationId: result.newAddress.locationId.id,
                  cellPhoneNumber: result.newAddress.cellPhoneNumber
                    ? result.newAddress.cellPhoneNumber.replace(/\D+/g, '')
                    : null,
                  homePhoneNumber: result.newAddress.homePhoneNumber
                    ? result.newAddress.homePhoneNumber.replace(/\D+/g, '')
                    : null,
                };

                this.apiService
                  .post<Address>(`v/1/associates/associates/${associateId}/addresses`, updatedAddress)
                  .subscribe((newAddress: Address) => {
                    this.dialogData.associateForm.patchValue(
                      {
                        ...this.dialogData.associateForm,
                        id: newAssociate.id,
                        firstName: newAssociate.firstName,
                        lastName: newAssociate.lastName,
                        emailAddress: newAssociate.emailAddress,
                        classId: newAssociate.classId,
                        messagePreferenceId: newAssociate.messagePreferenceId,
                      },
                      { emitEvent: false }
                    );
                    this.dialogData.addressForm.patchValue({
                      ...this.dialogData.addressForm,
                      associateId: newAssociate.id,
                      addressLine1: newAddress.addressLine1,
                      homePhoneNumber: newAddress.homePhoneNumber,
                      cellPhoneNumber: newAddress.cellPhoneNumber,
                      countryId: newAddress.countryId,
                      postalZipCode: newAddress.postalZipCode,
                      provStateId: newAddress.provStateId,
                      locationId: newAddress.locationId,
                    });

                    this.dialogData.vehicleForm.patchValue({
                      associateId: newAssociate.id,
                      name: `${newAssociate.lastName}, ${newAssociate.firstName}`,
                    });
                  });

                const customer: Customer = {
                  ...new Customer(),
                  associateId: newAssociate.id,
                };

                this.apiService.post<Customer>(`v/1/associates/customers`, customer).subscribe();
              }
            });
        }
      });
    } else {
      if (filteredAssociates) {
        const selectedAssociate = filteredAssociates.find((associate) => associate.id == value);
        this.selectedAssoc = selectedAssociate;

        this.dialogData.vehicleForm.patchValue({
          associateId: value,
          name: selectedAssociate.firstName
            ? `${selectedAssociate.lastName}, ${selectedAssociate.firstName}`
            : `${selectedAssociate.fullName}`,
        });

        this.dialogData.associateForm.patchValue(
          {
            ...this.dialogData.associateForm,
            id: `${value}`,
            firstName: `${selectedAssociate.firstName}`,
            lastName: `${selectedAssociate.lastName}`,
            emailAddress: `${selectedAssociate.emailAddress}`,
            classId: selectedAssociate.classId,
            messagePreferenceId: selectedAssociate.messagePreferenceId,
          },
          { emitEvent: false }
        );

        this.dialogData.addressForm.patchValue({
          ...this.dialogData.addressForm,
          associateId: value,
          addressLine1: selectedAssociate.addressLine1,
          homePhoneNumber: selectedAssociate.homePhoneNumber,
          cellPhoneNumber: selectedAssociate.cellPhoneNumber,
          countryId: selectedAssociate.countryId,
          postalZipCode: selectedAssociate.postalZipCode,
          provStateId: selectedAssociate.provStateId,
          locationId: selectedAssociate.locationId,
        });
      }
    }

    this.dialogData.filteredAssociates$.next([]);
  }

  displayAssociateName(filteredAssociates: AssociateReport[], value: string) {
    let displayAssociate: AssociateReport;
    if (filteredAssociates.length > 0) {
      displayAssociate = filteredAssociates.find((associate: AssociateReport) => associate.id === value);
      if (displayAssociate) {
        return displayAssociate.firstName
          ? `${displayAssociate.lastName}, ${displayAssociate.firstName}`
          : `${displayAssociate.fullName}`;
      } else {
        return value;
      }
    } else {
      return value;
    }
  }

  cancel() {
    this.dialogRef.close();
  }

  save(vehicleForm: FormGroup, associateForm: FormGroup, addressForm: FormGroup) {
    this.formSubmitted = true;

    let currentMileage: any = vehicleForm.get('currentMileage').value;

    if (typeof currentMileage === 'string') {
      const unmaskedOdometer = currentMileage ? parseInt(currentMileage.replace(',', '')) : null;
      currentMileage = unmaskedOdometer;
      vehicleForm.get('currentMileage').patchValue(unmaskedOdometer);
    }

    if (vehicleForm.valid) {
      this.dialogRef.close({ vehicleForm, associateForm, addressForm });
    }
  }
}
